import {
	CloseOutlined,
	ExpandOutlined,
	LogoutOutlined,
	MenuOutlined,
	QuestionCircleOutlined,
	ShrinkOutlined,
} from "@ant-design/icons"
import { Avatar } from "components/Avatar/Avatar"
import { Badge } from "components/Badge/Badge"
import { Button } from "components/Button/Button"
import { Drawer } from "components/Drawer/Drawer"
import { Dropdown } from "components/Dropdown/Dropdown"
import {
	type ItemType,
	Menu,
	type MenuItemType,
	type MenuProps,
} from "components/Menu/Menu"
import { useDisclosure, UseDisclosureReturn } from "hooks/useDisclosure"
import { useResponsive } from "hooks/useResponsive"
import { useRightSidebar } from "hooks/useRightSidebar"
import { EmpoLogo } from "icons/EmpoLogo"
import { EmpoLogoComplete } from "icons/EmpoLogoComplete"
import { useAuth } from "modules/auth/Auth"
import { useHelpMenuOption } from "modules/help/useHelpMenuOption"
import { useZendesk } from "modules/zendesk/useZendesk"
import {
	ComponentProps,
	ReactNode,
	Suspense,
	useCallback,
	useMemo,
} from "react"
import { useLocation, useNavigate } from "react-router"
import { cn } from "styles/cn"
import { DEFAULT_PLACEHOLDER } from "utils/constants"

export const BaseLayout = Object.assign(Layout, {
	Header,
	SideNav,
	Main,
})

function Layout({ className, children, ...props }: ComponentProps<"div">) {
	const { rightSidebar } = useRightSidebar()

	return (
		<div id="layout-root" className="relative flex h-svh flex-col">
			<div className="relative flex min-h-0 grow">
				<div
					{...props}
					id="layout-content"
					className={cn(
						"@container/content relative grid h-full w-full grid-cols-1 grid-rows-[auto_1fr] lg:grid-cols-[1fr] xl:grid-cols-[280px_1fr]",
						className,
					)}
				>
					{children}
				</div>
				{rightSidebar && <RightSidebar />}
			</div>
		</div>
	)
}

function Header({
	children,
	actions,
	username = DEFAULT_PLACEHOLDER,
	items = EMPTY_ITEMS,
	visibleHelpItems = true,
	viewLegalItems = EMPTY_ITEMS,
	supportRequestsItems = EMPTY_ITEMS,
	supportRequestsUpdatesCount = 0,
	notificationSettingsItems = EMPTY_ITEMS,
}: ComponentProps<"header"> & {
	actions?: ReactNode
	username?: string
	items?: MenuItemType[]
	visibleHelpItems?: boolean
	viewLegalItems?: MenuItemType[]
	supportRequestsItems?: MenuItemType[]
	supportRequestsUpdatesCount?: number
	notificationSettingsItems?: MenuItemType[]
}) {
	const { signOut } = useAuth()
	const navigate = useNavigate()
	const zendesk = useZendesk()
	const selectedKeys = useMatchingPathKey(items, notificationSettingsItems)
	const helpDocItem = useHelpMenuOption()
	const signOutMenuItem = useMemo(
		() => ({
			...signOutItem,
			onClick: async () => {
				await signOut()
				zendesk.logoutUser()
				navigate("/login", { replace: true })
			},
		}),
		[navigate, signOut, zendesk],
	)
	const userMenuItems = useMemo(
		() => [...notificationSettingsItems, signOutMenuItem],
		[notificationSettingsItems, signOutMenuItem],
	)
	const SKIP_NAVIGATION = useMemo(
		() =>
			[
				signOutItem.key,
				helpDocItem.key,
				zendesk.items.map(({ key }) => key),
				viewLegalItems.map(({ key }) => key),
				supportRequestsItems.map(({ key }) => key),
				notificationSettingsItems.map(({ key }) => key),
			].flat(),
		[
			helpDocItem.key,
			notificationSettingsItems,
			supportRequestsItems,
			viewLegalItems,
			zendesk.items,
		],
	)
	const menuItems = useMemo(() => {
		if (!visibleHelpItems) {
			return userMenuItems
		}

		const menuItems: ItemType[] = [...items]
		if (items.length > 0) {
			menuItems.push({ type: "divider" })
		}
		menuItems.push(
			...viewLegalItems,
			...zendesk.items,
			...supportRequestsItems,
			helpDocItem,
			...notificationSettingsItems,
			signOutMenuItem,
		)

		return menuItems
	}, [
		helpDocItem,
		items,
		notificationSettingsItems,
		signOutMenuItem,
		supportRequestsItems,
		userMenuItems,
		viewLegalItems,
		visibleHelpItems,
		zendesk.items,
	])
	const handleHomeNavigation = useCallback(
		() => navigate("/check-authorization"),
		[navigate],
	)

	return (
		<header className="@container flex min-h-20 items-center justify-between gap-3 bg-white px-8 lg:col-span-1 lg:min-h-13 lg:bg-green-0 lg:px-6 xl:col-span-2">
			<EmpoLogo
				className="w-8 min-w-8 hover:cursor-pointer @5xl:hidden"
				onClick={handleHomeNavigation}
			/>
			<EmpoLogoComplete
				className="hidden h-12 min-h-12 hover:cursor-pointer @5xl:block"
				onClick={handleHomeNavigation}
			/>

			{children}

			<div className="flex items-center gap-3">
				{username && username !== DEFAULT_PLACEHOLDER && (
					<div className="hidden max-w-full place-content-end xl:flex">
						<Dropdown
							placement="bottomRight"
							trigger={["click"]}
							rootClassName="max-w-full! min-w-2xs!"
							menu={{
								selectable: true,
								selectedKeys,
								items: userMenuItems,
							}}
						>
							<div className="flex cursor-pointer items-center gap-2 whitespace-nowrap">
								{username}
								<Avatar className="size-8! lg:size-8!">{username}</Avatar>
							</div>
						</Dropdown>
					</div>
				)}

				<Dropdown
					placement="bottom"
					trigger={["click"]}
					rootClassName="max-w-full! min-w-2xs!"
					menu={{
						selectable: true,
						selectedKeys,
						"aria-label": "Application and help options",
						onClick: ({ key }) =>
							!SKIP_NAVIGATION.includes(key) ? navigate(key) : null,
						items: menuItems,
					}}
				>
					<Badge
						offset={[0, 6]}
						count={supportRequestsUpdatesCount}
						className="inline-block xl:hidden"
						classNames={{ indicator: "bg-blue-60" }}
					>
						<Button
							type="text"
							size="large"
							aria-label="Application and help options"
							icon={<MenuOutlined aria-hidden />}
						/>
					</Badge>
				</Dropdown>

				{actions}
			</div>
		</header>
	)
}

function SideNav({
	className,
	items = EMPTY_ITEMS,
	viewLegalItems = EMPTY_ITEMS,
	supportRequestsItems = EMPTY_ITEMS,
	supportRequestsUpdatesCount = 0,
	notificationSettingsItems = EMPTY_ITEMS,
	...props
}: ComponentProps<"nav"> & {
	items: MenuProps["items"]
	viewLegalItems?: MenuItemType[]
	supportRequestsItems?: MenuItemType[]
	supportRequestsUpdatesCount?: number
	notificationSettingsItems?: MenuItemType[]
}) {
	const navigate = useNavigate()
	const zendesk = useZendesk()
	const matchingKey = useMatchingPathKey(items, notificationSettingsItems)
	const helpDocItem = useHelpMenuOption()
	const menuItems: MenuItemType[] = [
		...viewLegalItems,
		...zendesk.items,
		...supportRequestsItems,
		helpDocItem,
	]
	const menuHelpLabel =
		supportRequestsUpdatesCount > 0 ? (
			<>
				Help
				<Badge
					count={supportRequestsUpdatesCount}
					classNames={{ indicator: "ml-2 bg-blue-60" }}
				/>
			</>
		) : (
			"Help"
		)

	return (
		<nav
			{...props}
			aria-label="Sidebar navigation"
			className={cn(
				"hidden w-full min-w-2xs flex-col justify-between border-r border-r-grey-20 pt-6 xl:row-start-2 xl:flex",
				className,
			)}
		>
			<Menu
				items={items}
				mode="vertical"
				selectedKeys={matchingKey}
				className="flex flex-col border-0"
				aria-label="Application links"
				onSelect={({ key }) => navigate(key)}
			/>

			<Menu
				selectable={false}
				items={[
					{ type: "divider" },
					{
						key: "help",
						label: menuHelpLabel,
						icon: <QuestionCircleOutlined aria-hidden />,
						children: menuItems,
					},
				]}
				mode="vertical"
				className="border-0"
				aria-label="Help links"
			/>
		</nav>
	)
}

function Main({ className, ...props }: ComponentProps<"main">) {
	return (
		<main
			{...props}
			className={cn(
				"overflow-auto p-4 lg:col-span-1 lg:col-start-1 lg:row-start-2 xl:col-span-1 xl:col-start-2 xl:px-12 xl:py-8",
				className,
			)}
		/>
	)
}

function RightSidebar() {
	const { setSidebarState, rightSidebar } = useRightSidebar()
	const disclosure = useDisclosure({ defaultIsOpen: true })
	const responsive = useResponsive()

	if (!rightSidebar) {
		return null
	}

	return (
		<>
			{responsive["lg"] && (
				<aside
					aria-label={rightSidebar.title}
					className="flex max-w-lg min-w-lg flex-col gap-5 border-l border-grey-20 bg-white p-5"
				>
					<SiderHeader title={rightSidebar.title} />
					<Suspense fallback={null}>
						<rightSidebar.component />
					</Suspense>
				</aside>
			)}

			{!responsive["lg"] && (
				<Drawer
					open
					getContainer={false}
					placement="bottom"
					size="448"
					mask={{ closable: false, enabled: false }}
					title={
						<SiderHeader title={rightSidebar.title} disclosure={disclosure} />
					}
					aria-label={rightSidebar.title}
					classNames={{
						header: "border-0 p-0",
						body: "flex flex-col gap-4 p-0",
						// Reduce vertical padding when collapsed so SiderHeader fits inside fixed collapsed height (!h-17)
						section: cn("rounded-t-xl", !disclosure.isOpen && "py-3!"),
						wrapper: cn(
							"!h-[calc(100%-(--spacing(30)))] rounded-t-xl",
							!disclosure.isOpen && "!h-17",
						),
					}}
					onClose={() => setSidebarState(null)}
				>
					<Suspense fallback={null}>
						<rightSidebar.component />
					</Suspense>
				</Drawer>
			)}
		</>
	)
}

function SiderHeader({
	title,
	disclosure,
}: {
	title: string
	disclosure?: Pick<UseDisclosureReturn, "toggle" | "isOpen">
}) {
	const { setSidebarState } = useRightSidebar()
	const Icon = disclosure?.isOpen ? ShrinkOutlined : ExpandOutlined
	const ariaLabel = disclosure?.isOpen ? "Collapse" : "Expand"

	return (
		<div className="flex items-center justify-between gap-3">
			<div id="sidebar-back-slot" className="contents" />

			<h2 className="mr-auto font-headings text-xl font-semibold">{title}</h2>

			{disclosure && (
				<Button
					type="text"
					icon={<Icon aria-hidden />}
					aria-label={ariaLabel}
					className="ml-auto text-navy-60"
					onClick={disclosure?.toggle}
				/>
			)}

			<Button
				type="text"
				icon={<CloseOutlined aria-hidden />}
				aria-label={`Close ${title}`}
				className="size-8 text-navy-60"
				onClick={() => setSidebarState(null)}
			/>
		</div>
	)
}

function useMatchingPathKey(
	items: ItemType[] = EMPTY_ITEMS,
	extraItems: ItemType[] = EMPTY_ITEMS,
) {
	const location = useLocation()

	return useMemo(() => {
		const matches = [...items, ...extraItems].filter((i) =>
			location.pathname.includes(i?.key as string),
		)
		if (matches.length === 0) {
			return EMPTY_KEYS
		}
		return [matches[matches.length - 1]?.key as string]
	}, [extraItems, items, location.pathname])
}

const EMPTY_ITEMS: MenuItemType[] = []
const EMPTY_KEYS: string[] = []

const signOutItem = {
	key: "sign-out",
	label: "Sign out",
	icon: <LogoutOutlined aria-hidden />,
}
